import React, { useState, useRef, useCallback, useEffect } from "react";
import Autosuggest from "react-autosuggest";
import PropTypes from "prop-types";

const _Suggestions = (props) => {
  const {
    handleChange,
    options,
    title = "",
    placeholder = "",
    errors = "",
    isError = false,
  } = props;
  const selectRef = useRef(null);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const handleClickOutside = useCallback((e) => {
    if (selectRef.current && !selectRef.current.contains(e.target)) {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const onChange = (event, { newValue }) => {
    setValue(newValue);
  };

  const getSuggestions = (valueToFilter) => {
    const inputValue = (valueToFilter || "").trim().toLowerCase();
    if (!options || options.length === 0) return [];
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue)
    );
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(value));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion) {
      handleChange(suggestion.value);
      setValue(suggestion.value);
      setSuggestions([]);
    }
  };

  const inputProps = {
    placeholder: placeholder,
    value: value,
    onChange: onChange,
    onFocus: () => onSuggestionsFetchRequested({ value }),
  };

  const getSuggestionValue = (suggestion) => suggestion.value;

  const renderSuggestion = (suggestion) => <div>{suggestion.label}</div>;

  const themeCustom = {
    container: "w-full",
    suggestionsContainer:
      "absolute mt-1 bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto",
    suggestionsList: "list-none p-2 m-0",
    suggestion:
      "px-3 py-2 cursor-pointer rounded-md hover:bg-blue-50 hover:border-blue-100 transition-all duration-200 capitalize sm:text-sm",
    suggestionHighlighted: "bg-blue-500 text-gray-800",
  };

  const renderInputComponent = (inputProps) => (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        {...inputProps}
        className="form-input flex-grow w-full rounded-lg border-primary/30 bg-background-light dark:bg-background-dark/50 py-2 pl-10 text-sm text-gray-900 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:ring-primary"
      />
    </div>
  );

  return (
    <div className="w-full py-2" ref={selectRef}>
      {title && (
        <label className="text-gray-700 font-semibold text-sm">{title}</label>
      )}

      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderInputComponent={renderInputComponent}
        inputProps={inputProps}
        theme={themeCustom}
        shouldRenderSuggestions={() => true}
        onSuggestionSelected={onSuggestionSelected}
      />

      {isError && errors && (
        <p className="text-red-500 text-xs mt-1">{errors}</p>
      )}
    </div>
  );
};

_Suggestions.propTypes = {
  handleChange: PropTypes.func,
  options: PropTypes.array,
  title: PropTypes.any,
  placeholder: PropTypes.any,
  errors: PropTypes.any,
  isError: PropTypes.bool,
};

export default _Suggestions;
